/**
 * BPM Display Component
 * Shows heart rate with zone visualization
 */

import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BPMZone } from '@/services/bpm-service';
import { Heart } from 'lucide-react';

interface BPMDisplayProps {
  currentBPM: number;
  avgBPM: number;
  maxBPM: number;
  zone: BPMZone | null;
  isMonitoring: boolean;
}

export function BPMDisplay({ currentBPM, avgBPM, maxBPM, zone, isMonitoring }: BPMDisplayProps) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-destructive" />
          <h3 className="font-semibold">Heart Rate</h3>
        </div>
        {!isMonitoring && (
          <Badge variant="outline">Not Monitoring</Badge>
        )}
      </div>

      <div className="space-y-4">
        {/* Current BPM */}
        <div className="text-center">
          <div className="text-5xl font-bold" style={{ color: zone?.color || 'inherit' }}>
            {currentBPM}
          </div>
          <div className="text-sm text-muted-foreground">BPM</div>
          {zone && (
            <Badge
              className="mt-2"
              style={{ backgroundColor: zone.color }}
            >
              {zone.name} Zone
            </Badge>
          )}
        </div>

        {/* Stats */}
        {isMonitoring && (
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="text-center">
              <div className="text-2xl font-semibold">{avgBPM}</div>
              <div className="text-xs text-muted-foreground">Average</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold">{maxBPM}</div>
              <div className="text-xs text-muted-foreground">Maximum</div>
            </div>
          </div>
        )}

        {/* Zone Guide */}
        {zone && (
          <div className="pt-4 border-t">
            <p className="text-xs text-muted-foreground">
              {zone.min.toFixed(0)} - {zone.max.toFixed(0)} BPM
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
